import { z } from 'zod';
import { saveUser, UserProfile } from 'communicator/store';
import { getApiKey, getIsNonVisaRecognizedUser, getSessionId } from 'communicator/store/selectors';
import { generateAccessTokenFromIdToken } from 'communicator/utils/api/token';
import { IssuedTokens } from 'types/api/b2ciam';
import { TokenResponse } from 'types/api/token';
import { FetchResponseValue } from 'types/fetch';

export function getIdToken(issuedTokens: IssuedTokens) {
  for (const issuedToken of issuedTokens) {
    if (
      issuedToken.token_type_hint === 'urn:ext:oauth:token-type-hint:id_token' ||
      issuedToken.token_type_hint === 'urn:ext:ietf:params:oauth:response-type:id_token'
    ) {
      return issuedToken.token;
    }
  }

  return null;
}

type ParsedIdToken = {
  emailHash?: string;
  amrCookie?: string[];
};

export function parseIdToken(idToken: string | null): ParsedIdToken {
  if (!idToken) {
    return {};
  }

  const parsed: ParsedIdToken = {};

  try {
    const rawCookieValue = JSON.parse(window.atob(idToken.split('.')[1]));
    parsed.emailHash = z.string().parse(rawCookieValue.email);
    parsed.amrCookie = z.string().array().parse(rawCookieValue.amr);
    return parsed;
  } catch (_) {
    return parsed;
  }
}

type ConvertIdTokenOptions = {
  idToken: string;
  marks: {
    token: string;
  };
};

export default async function convertIdTokenToAccessToken({
  idToken,
  marks
}: ConvertIdTokenOptions): Promise<FetchResponseValue<TokenResponse>> {
  const apikey = getApiKey();
  const sessionId = getSessionId();

  const tokenResponse = await generateAccessTokenFromIdToken({
    apikey,
    idToken,
    mark: marks.token,
    sessionId
  });

  if (tokenResponse.ok) {
    const responseStatus = tokenResponse.data.data?.probeType?.[0];
    const isTnc = responseStatus === 'TERMS';

    const userProfileUpdates: UserProfile = {
      accessToken: {
        authLevel: tokenResponse.data.auth_data?.authenticator_assurances?.[0]?.level,
        expiresIn: tokenResponse.data.expires_in,
        isPartial: false,
        token: tokenResponse.data.access_token
      },
      country: tokenResponse.data.country,
      locale: tokenResponse.data.locale,
      tnc: {
        status: isTnc ? 'not_accepted' : null
      }
    };

    const { amrCookie, emailHash } = parseIdToken(idToken);

    if (getIsNonVisaRecognizedUser(amrCookie)) {
      userProfileUpdates.isNonVisaRecognizedUser = true;
    }

    if (emailHash) {
      userProfileUpdates.emailHash = emailHash;
    }

    saveUser(userProfileUpdates);
    return tokenResponse;
  }

  return tokenResponse;
}
